import { CartAddEvent, CartErrorEvent } from '@theme/events';

const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

class GreetingCardAddOn extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {number | undefined} */
  errorTimeout;

  /** @type {boolean} */
  busy = false;

  /** @type {number} */
  lastItemCount = 0;

  connectedCallback() {
    this.form = this.getForm();
    this.form?.addEventListener('submit', this.handleSubmit, true);
    this.addEventListener('change', this.handleSelectionChange);
    this.querySelector('[data-card-message]')?.addEventListener('input', this.updateMessageCount);
    this.updateMessageCount();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.handleSubmit, true);
    this.removeEventListener('change', this.handleSelectionChange);
    this.querySelector('[data-card-message]')?.removeEventListener('input', this.updateMessageCount);
    if (this.errorTimeout) clearTimeout(this.errorTimeout);
  }

  getForm() {
    const formId = this.dataset.formId;
    if (!formId) return this.closest('form');
    const form = document.getElementById(formId);
    return form instanceof HTMLFormElement ? form : this.closest('form');
  }

  getSelectedCard() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('.greeting-card-add-on__input:checked'));
  }

  getProductFormComponent() {
    return this.form?.closest('product-form-component') || null;
  }

  handleSelectionChange = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'greeting_card_selection') return;
    this.hideError();
  };

  updateMessageCount = () => {
    const textarea = /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[data-card-message]'));
    const counter = this.querySelector('[data-card-message-count]');
    if (!textarea || !counter) return;

    counter.textContent = `${textarea.value.length} / ${textarea.maxLength}`;
  };

  handleSubmit = async (event) => {
    const selectedCard = this.getSelectedCard();
    if (!selectedCard || selectedCard.dataset.cardType !== 'premium') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.busy || !this.form) return;

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const premiumVariantId = selectedCard.dataset.cardVariantId;
    if (!premiumVariantId || !/^\d+$/.test(premiumVariantId)) {
      this.showError('Premium card variant ID is missing. Add the card product to the premium greeting cards collection.');
      return;
    }

    const quantityValidation = this.getQuantityValidation();
    if (quantityValidation && !quantityValidation.canAdd) {
      this.showError(quantityValidation.message || 'This quantity is not available.');
      return;
    }

    this.setBusy(true);

    try {
      const payload = this.buildCartPayload(selectedCard, premiumVariantId);
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const responseData = await response.json().catch(() => ({}));

      if (!response.ok || responseData.status) {
        throw responseData;
      }

      const cart = await fetch('/cart.js').then((cartResponse) => cartResponse.json()).catch(() => undefined);
      this.hideError();
      this.dispatchCartAdd(cart, responseData.sections, payload.items.length);
    } catch (error) {
      this.handleCartError(error);
    } finally {
      this.setBusy(false);
    }
  };

  getQuantityValidation() {
    const productForm = this.getProductFormComponent();
    const quantitySelector = productForm?.refs?.quantitySelector;
    const validation = quantitySelector?.canAddToCart?.();
    if (!validation || validation.canAdd) return validation;

    const template = productForm?.dataset.quantityErrorMax || '';
    return {
      ...validation,
      message: template.replace('{{ maximum }}', validation.maxQuantity?.toString() || ''),
    };
  }

  buildCartPayload(selectedCard, premiumVariantId) {
    const formData = new FormData(this.form);
    const mainVariantId = formData.get('id')?.toString();
    const quantity = this.getQuantity(formData);
    const properties = this.getLineItemProperties(formData);
    const sections = this.getCartSectionIds();

    if (!mainVariantId) {
      throw { message: 'Main product variant is missing.' };
    }

    const mainItem = {
      id: Number(mainVariantId),
      quantity,
      properties,
    };

    const sellingPlan = formData.get('selling_plan')?.toString();
    if (sellingPlan) {
      mainItem.selling_plan = Number(sellingPlan);
    }

    const premiumItem = {
      id: Number(premiumVariantId),
      quantity: 1,
      properties: {
        _greeting_card_addon: 'true',
        _greeting_card_for_product: this.dataset.productTitle || '',
        _greeting_card_for_variant: mainVariantId,
        _greeting_card_title: selectedCard.dataset.cardTitle || selectedCard.value,
      },
    };

    if (properties['Card message']) {
      premiumItem.properties._greeting_card_message = properties['Card message'];
    }

    const payload = {
      items: [mainItem, premiumItem],
      sections,
      sections_url: window.location.pathname,
    };

    this.lastItemCount = quantity + 1;

    return payload;
  }

  getQuantity(formData) {
    const productForm = this.getProductFormComponent();
    const quantityFromSelector = Number(productForm?.refs?.quantitySelector?.getValue?.());
    const quantityFromForm = Number(formData.get('quantity'));
    const quantityFromDefault = Number(productForm?.dataset.quantityDefault);

    return quantityFromSelector || quantityFromForm || quantityFromDefault || 1;
  }

  getLineItemProperties(formData) {
    const properties = {};

    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (!match) continue;
      if (typeof value !== 'string') continue;

      const trimmedValue = value.trim();
      if (trimmedValue === '') continue;

      properties[match[1]] = trimmedValue;
    }

    return properties;
  }

  getCartSectionIds() {
    const sectionIds = new Set();

    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.add(item.dataset.sectionId);
      }
    });

    return Array.from(sectionIds).join(',');
  }

  setBusy(isBusy) {
    this.busy = isBusy;
    this.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    const productForm = this.getProductFormComponent();
    productForm?.querySelectorAll('button[type="submit"]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;

      if (isBusy) {
        if (!button.disabled) button.dataset.greetingCardWasEnabled = 'true';
        button.disabled = true;
      } else if (button.dataset.greetingCardWasEnabled === 'true') {
        button.disabled = false;
        delete button.dataset.greetingCardWasEnabled;
      }
    });
  }

  dispatchCartAdd(cart, sections, itemCount) {
    const productForm = this.getProductFormComponent();
    this.dispatchEvent(
      new CartAddEvent(cart, this.form?.id || this.id, {
        source: 'product-form-component',
        itemCount: this.lastItemCount || itemCount,
        productId: productForm?.dataset.productId,
        sections,
      })
    );
  }

  handleCartError(error) {
    const fallbackMessage = 'The greeting card could not be added. Please try again.';
    const message = this.normalizeErrorMessage(error, fallbackMessage);
    const productForm = this.getProductFormComponent();

    this.showError(message);
    this.dispatchEvent(new CartErrorEvent(this.form?.id || this.id, message, error?.description, error?.errors));

    const liveRegion = productForm?.querySelector('[ref="liveRegion"]');
    if (liveRegion) {
      liveRegion.textContent = message;
      window.setTimeout(() => {
        liveRegion.textContent = '';
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }
  }

  normalizeErrorMessage(error, fallbackMessage) {
    if (!error) return fallbackMessage;
    if (typeof error === 'string') return error;
    if (typeof error.message === 'string') return error.message;
    if (typeof error.description === 'string') return error.description;
    if (typeof error.errors === 'string') return error.errors;
    return fallbackMessage;
  }

  showError(message) {
    const errorElement = this.querySelector('[data-card-addon-error]');
    if (!errorElement) return;

    if (this.errorTimeout) clearTimeout(this.errorTimeout);
    errorElement.textContent = message;
    errorElement.hidden = false;

    this.errorTimeout = window.setTimeout(() => {
      this.hideError();
    }, ERROR_MESSAGE_DISPLAY_DURATION);
  }

  hideError() {
    const errorElement = this.querySelector('[data-card-addon-error]');
    if (!errorElement) return;

    errorElement.hidden = true;
    errorElement.textContent = '';
  }
}

if (!customElements.get('greeting-card-add-on')) {
  customElements.define('greeting-card-add-on', GreetingCardAddOn);
}
